import type {
  AuthResponse,
  LoginInput,
  LoginResponse,
  PublicCaptchaConfigDTO,
  SignupInput,
  SignupResponse,
  UserDTO,
} from '@bruno/shared';
import { isTwoFactorChallenge } from '@bruno/shared';
import { api, setAccessToken } from './client';

/**
 * Signup no longer establishes a session: the account is created
 * unverified and a confirmation email is sent. Callers show a
 * "check your inbox" screen; the user can sign in only after confirming.
 */
export async function signup(input: SignupInput): Promise<SignupResponse> {
  return api<SignupResponse>('/v1/auth/signup', { method: 'POST', body: input });
}

/**
 * Confirms an email address using the token from the emailed link and
 * (on success) signs the user in by setting the returned access token.
 */
export async function verifyEmail(token: string): Promise<AuthResponse> {
  const res = await api<AuthResponse>('/v1/auth/verify-email', {
    method: 'POST',
    body: { token },
    retryOn401: false,
  });
  setAccessToken(res.accessToken);
  return res;
}

/** Requests a fresh confirmation email. Always resolves (constant 204). */
export async function resendVerification(
  email: string,
  captchaToken?: string,
): Promise<void> {
  await api<void>('/v1/auth/resend-verification', {
    method: 'POST',
    body: { email, captchaToken },
    retryOn401: false,
  });
}

/**
 * Returns either a regular AuthResponse (user signed in) OR a 2FA
 * challenge ticket the caller must POST to /v1/auth/2fa/login to
 * complete the sign-in. The access token is set only on the success
 * branch.
 */
export async function login(input: LoginInput): Promise<LoginResponse> {
  const res = await api<LoginResponse>('/v1/auth/login', { method: 'POST', body: input });
  if (!isTwoFactorChallenge(res)) {
    setAccessToken(res.accessToken);
  }
  return res;
}

export async function logout(): Promise<void> {
  await api<void>('/v1/auth/logout', { method: 'POST', retryOn401: false });
  setAccessToken(null);
}

export async function getMe(): Promise<{ user: UserDTO }> {
  return api<{ user: UserDTO }>('/v1/auth/me');
}

export async function tryRestoreSession(): Promise<UserDTO | null> {
  try {
    const res = await api<AuthResponse>('/v1/auth/refresh', { method: 'POST', retryOn401: false });
    setAccessToken(res.accessToken);
    return res.user;
  } catch {
    return null;
  }
}

export async function getCaptchaConfig(): Promise<PublicCaptchaConfigDTO> {
  return api<PublicCaptchaConfigDTO>('/v1/auth/captcha-config', { retryOn401: false });
}
